import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import { nanoid } from 'nanoid';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { MailrailPartnerMessage } from '@feathr/blackbox';
import { CampaignClass } from '@feathr/blackbox';
import { CardContent, CardHeader, CardV2, Fieldset, FormElement, toast } from '@feathr/components';
import CampaignSelect from '@feathr/extender/components/CampaignSelect';
import RecipientsPicker from '@feathr/extender/components/RecipientsPicker';
import { useStore } from '@feathr/extender/state';
import { useId } from '@feathr/hooks';

export interface IProps {
  campaign: MailrailPartnerMessage;
  disabled: boolean;
}

function MailrailPartnerAudienceSection({ campaign, disabled }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Partners } = useStore();
  const eventId = campaign.get('event');
  const elementId = useId();
  const [isFilteringPartners, setIsFilteringPartners] = useState(false);

  async function handleSelectCampaign(newCampaign: string): Promise<void> {
    const participation = campaign.get('participation');
    campaign.set({
      invites_campaign: newCampaign,
      participation: {
        ...participation,
        mode: participation.mode === 'manual' ? 'manual' : 'campaign',
      },
    });
    await filterParticipation(newCampaign);
  }

  function handleClearCampaign(): void {
    const participation = campaign.get('participation');
    campaign.set({
      invites_campaign: undefined,
      participation: {
        ...participation,
        mode: participation.mode === 'manual' ? 'manual' : 'event',
      },
    });
  }

  async function filterParticipation(campaignId: string): Promise<void> {
    const partnerIds = campaign.get('participation').partner_ids;
    if (!partnerIds || partnerIds.length === 0) {
      return;
    }
    setIsFilteringPartners(true);
    try {
      const listId = `filter-participation-${nanoid(12)}`;
      const filteredPartners = Partners.list(
        {
          filters: { id__in: partnerIds, participation: campaignId },
          only: ['id'],
          pagination: { page_size: 1000 },
        },
        {},
        listId,
      );
      await when(() => !filteredPartners.isPending);
      while (
        !filteredPartners.isErrored &&
        filteredPartners.pagination.page < filteredPartners.pagination.pages
      ) {
        filteredPartners.fetchMore();
        await when(() => !filteredPartners.isPending);
      }
      if (filteredPartners.isErrored) {
        toast(t("Couldn't filter partners by campaign. Try again."), { type: 'error' });
        return;
      }
      const filteredPartnerIds = filteredPartners.models.map((p) => p.id);
      campaign.set({
        participation: {
          ...campaign.get('participation'),
          partner_ids: filteredPartnerIds,
        },
      });
    } finally {
      setIsFilteringPartners(false);
    }
  }

  return (
    <>
      <CardV2 width={'full'}>
        <CardHeader
          description={
            <Trans t={t}>
              When a partner opens a message associated with an Invites Campaign, the partner will
              advance to the "Received message" status in that campaign.{' '}
              <strong>
                Selecting a campaign will limit the possible recipients of this message to the
                participating partners of that campaign.
              </strong>
            </Trans>
          }
          title={t('Campaign (Optional)')}
        />
        <CardContent>
          <Fieldset>
            <FormElement htmlFor={elementId} label={t('Campaign')}>
              <CampaignSelect
                disabled={disabled || isFilteringPartners}
                eventId={eventId}
                id={elementId}
                isMulti={false}
                onChange={handleSelectCampaign}
                onClear={handleClearCampaign}
                placeholder={t('Select a campaign')}
                type={[CampaignClass.Referral]}
                value={campaign.get('invites_campaign')}
              />
            </FormElement>
          </Fieldset>
        </CardContent>
      </CardV2>
      <CardV2 width={'full'}>
        <CardHeader
          description={t('Select the partners you would like to message.')}
          title={t('Audience')}
        />
        <CardContent>
          <RecipientsPicker disabled={disabled || isFilteringPartners} model={campaign} />
        </CardContent>
      </CardV2>
    </>
  );
}

export default observer(MailrailPartnerAudienceSection);
